import { useState } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, LoadingState, TabBar } from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { useGetSectorById, useGetSectorSumarize } from '@practium/features/api';
import styles from './SectorFloraSummaryPageClient.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type FloraSummaryTab = 'sector-analysis' | 'soil-history' | 'leaf-analysis';

const SummaryText = ({ text }: { text?: string }) => {
	if (!text) {
		return (
			<div className={styles.summaryText}>
				<p>Nenhuma análise disponível.</p>
			</div>
		);
	}

	return (
		<div className={styles.summaryText}>
			<ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
		</div>
	);
};


export const SectorFloraSummaryPageClient = () => {
	const navigate = useNavigate();
	const { id = '' } = useParams();
	const [searchParams] = useSearchParams();
	const propId = searchParams.get('propId') || '';
	const [activeTab, setActiveTab] = useState<FloraSummaryTab>('sector-analysis');

	const sectorQuery = useGetSectorById(id, { enabled: !!id });
	const sector = sectorQuery.data;
	const sectorName = sector?.formData.name;
	const referenceId = id || '—';

	const sectorSummaryQuery = useGetSectorSumarize(referenceId);
	const sectorSumarize = sectorSummaryQuery.data;

	const tabs: SectionTab<FloraSummaryTab>[] = [
		{
			key: 'sector-analysis',
			label: 'Análise de Setor',
			content: <SummaryText text={sectorSumarize?.soilAnalyses} />,
		},
		{
			key: 'soil-history',
			label: 'Histórico de Solo (Manutenção)',
			content: <SummaryText text={sectorSumarize?.soilAnalysisHistory} />,
		},
		{
			key: 'leaf-analysis',
			label: 'Análise Foliar',
			content: <SummaryText text={sectorSumarize?.leafTissueAnalysis} />,
		},
	];

	const activeContent = tabs.find((tab) => tab.key === activeTab)?.content;

	const handleBack = () => {
		if (id) {
			navigate(`/propriedades/ver?type=sectors&id=${id}`);
			return;
		}
		if (propId) {
			navigate(`/propriedades?id=${propId}&tab=sectors`);
			return;
		}
		navigate('/propriedades');
	};

	if (sectorQuery.isLoading || sectorSummaryQuery.isLoading) return <LoadingState />;

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>Resumo de IA do Setor</h1>
					<div className={styles.reference}>
						<span>ID Ref:</span>
						<strong>{referenceId}</strong>
					</div>
					{sectorName && <p className={styles.sectorName}>{sectorName}</p>}
				</div>
				<Button
					variant="outline"
					icon={<ArrowBackOutlined />}
					className={styles.backButton}
					onClick={handleBack}
				>
					Voltar
				</Button>
			</div>

			<div className={styles.card}>
				<TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
				<div className={styles.content}>{activeContent}</div>
			</div>
		</div>
	);
};
